import * as React from "react";
import { useState } from "react";
import { toastMultilineErrors } from "../../../util";
import {
  validateStep,
  createSchemasByMode,
} from "../../../validation/abstractSchema";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

interface Step4Props {
  formData: any;
  maxCorrespondingAuthors?: number;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

const ABSTRACT_PREVIEW_CSS = `
  #abstractPreview ul,
  #abstractPreview ol {
    list-style-position: inside !important;
    padding-left: 0 !important;
    margin: 0 !important;
  }
  #abstractPreview ul {
    list-style: disc;
  }
  #abstractPreview ol {
    list-style: decimal;
  }
  #abstractPreview p {
    margin-bottom: 0;
  }
`;

const Step4: React.FC<Step4Props> = ({
  formData,
  maxCorrespondingAuthors = 2,
  onBack,
  onSubmit,
  isSubmitting,
}) => {
  const handleSubmit = async () => {
    // Final validation using Zod schema
    const schemas = createSchemasByMode("html", maxCorrespondingAuthors);
    const finalSchema = schemas.step1.merge(schemas.step2).merge(schemas.step3);

    const validation = validateStep(formData, finalSchema);

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors || {});

      toastMultilineErrors(errorMessages);
      return;
    }

    // Confirmation dialog
    const confirmSubmission = confirm(
      "Are you ready to submit your abstract?\n\n" +
        "Once submitted, no further modifications will be permitted. " +
        "Please ensure all information is accurate and complete.\n\n" +
        "Click OK to submit or Cancel to review your abstract.",
    );

    if (!confirmSubmission) {
      return;
    }

    try {
      await onSubmit();
    } catch (error) {
      console.error("Submission error:", error);

      let errorMessage = "Failed to submit abstract. Please try again.";

      // Try to extract more specific error message
      if (error instanceof Response) {
        const errorData = await error.json().catch(() => ({}));
        errorMessage = errorData.error || errorData.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toastMultilineErrors([errorMessage]);
    }
  };

  // Get authors with their details
  const getAuthors = () => {
    const authors = formData.authors || [];
    const allInstitutions = formData.institutions || [];

    return authors
      .map((author: any, index: number) => {
        const institutions =
          author.abstract_author_institutions_attributes || [];

        // Map institution IDs to their letter indices (a, b, c, etc.)
        const institutionLetters = institutions
          .map((inst: any) => {
            const institutionName = inst.abstract_institution_id;
            const globalIndex = allInstitutions.indexOf(institutionName);
            return globalIndex !== -1
              ? String.fromCharCode(97 + globalIndex)
              : null;
          })
          .filter(Boolean);

        const isCorresponding = author.is_corresponding || false;

        return {
          name: `${author.first_name} ${author.last_name}`,
          institutionLetters,
          email: author.corresponding_email,
          isCorresponding,
          position: author.position || index + 1,
        };
      })
      .sort((a: any, b: any) => a.position - b.position); // Sort by position
  };

  const authors = getAuthors();
  const correspondingAuthor = authors.find((a: any) => a.isCorresponding);

  // Format institutions for display
  const allInstitutions = formData.institutions || [];
  const formAuthors = formData.authors || [];

  const usedInstitutionIndexes = new Set();

  formAuthors.forEach((author: any) => {
    (author.abstract_author_institutions_attributes || []).forEach(
      (inst: any) => {
        const institutionName = inst.abstract_institution_id;
        const globalIndex = allInstitutions.indexOf(institutionName);

        if (globalIndex !== -1) {
          usedInstitutionIndexes.add(globalIndex);
        }
      },
    );
  });

  const institutionsToDisplay = allInstitutions
    .map((institution: string, index: number) => ({ institution, index }))
    .filter(({ index }: any) => usedInstitutionIndexes.has(index));

  return (
    <div className="space-y-6">
      <style dangerouslySetInnerHTML={{ __html: ABSTRACT_PREVIEW_CSS }} />
      <div>
        <h2 className="mb-2 text-xl font-semibold">
          Preview & Confirm Submission
        </h2>
        <p className="text-gray-600">
          Once the submission is completed, no further amendments will be
          permitted. Please ensure that all information provided is accurate and
          complete. For any amendment requests, kindly contact the official
          committee.
        </p>
      </div>
      <div id="abstractPreview">
        <div style={{ overflowX: "auto", overflowY: "hidden" }}>
          <div
            style={{
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              border: "2px solid rgb(209 213 219)",
              borderRadius: "8px",
            }}
          >
            <div style={{ padding: "32px" }}>
              {/* Title */}
              <section
                style={{
                  fontFamily: "Times New Roman",
                  fontSize: "14pt",
                  fontWeight: 700,
                  textAlign: "center",
                  textTransform: "uppercase",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: formData.title || "" }}
                />
              </section>

              {/* Authors */}
              <section style={{ textAlign: "center", marginTop: "12pt" }}>
                <div
                  style={{
                    fontFamily: "Times New Roman",
                    fontSize: "12pt",
                    lineHeight: 1,
                  }}
                >
                  {authors.map((author: any, index: number) => (
                    <span key={index}>
                      <span
                        style={{
                          textDecoration:
                            author.position === 1 || author.position === "1"
                              ? "underline"
                              : "none",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{author.name}</span>
                        {(author.institutionLetters.length > 0 ||
                          author.isCorresponding) && (
                          <sup style={{ fontSize: "11pt" }}>
                            {author.institutionLetters.join(",")}
                            {author.institutionLetters.length > 0 &&
                            author.isCorresponding
                              ? ","
                              : ""}
                            {author.isCorresponding ? "*" : ""}
                          </sup>
                        )}
                      </span>
                      {index < authors.length - 1 && <span>{"  ,  "}</span>}
                    </span>
                  ))}
                </div>
              </section>

              {/* Institutions */}
              {institutionsToDisplay.length > 0 && (
                <section
                  style={{
                    textAlign: "center",
                    fontFamily: "Times New Roman",
                    fontSize: "11pt",
                    color: "rgb(75 85 99)",
                    fontStyle: "italic",
                  }}
                >
                  {institutionsToDisplay.map(
                    ({ institution, index }: any, displayIndex: number) => (
                      <div key={index}>
                        <sup>{String.fromCharCode(97 + index)}</sup>
                        {institution}
                        {displayIndex < institutionsToDisplay.length - 1 && ","}
                      </div>
                    ),
                  )}
                </section>
              )}

              {/* Corresponding Author */}
              {correspondingAuthor && (
                <section
                  style={{
                    textAlign: "center",
                    fontFamily: "Times New Roman",
                    fontSize: "11pt",
                    color: "rgb(75 85 99)",
                    fontStyle: "italic",
                  }}
                >
                  <div>
                    Corresponding Author:{" "}
                    {authors
                      .filter((a: any) => a.isCorresponding)
                      .map((a: any) => a.email)
                      .join(", ")}
                  </div>
                </section>
              )}

              {/* Abstract Section */}
              <section style={{ marginTop: "12pt" }}>
                <div
                  style={{
                    fontFamily: "Times New Roman",
                    fontWeight: 700,
                    fontSize: "11pt",
                  }}
                >
                  Abstract
                </div>
                <div
                  style={{
                    fontFamily: "Times New Roman",
                    fontSize: "11pt",
                    textAlign: "justify",
                  }}
                  dangerouslySetInnerHTML={{ __html: formData.content || "" }}
                />
              </section>

              {/* Keywords */}
              {formData.keywords && formData.keywords.length > 0 && (
                <section style={{ marginTop: "12pt" }}>
                  <div style={{ fontFamily: "Times New Roman" }}>
                    <span style={{ fontSize: "11pt", fontWeight: 700 }}>
                      Keywords:
                    </span>
                    <span style={{ fontSize: "11pt", textAlign: "justify" }}>
                      {" "}
                      {formData.keywords.join(", ")}
                    </span>
                  </div>
                </section>
              )}

              {/* Images */}
              {formData.abstractImages &&
                formData.abstractImages.length > 0 && (
                  <section style={{ marginTop: "12pt" }}>
                    <div
                      style={{
                        display: "grid",
                        gap: "16px",
                        placeItems: "center",
                        gridTemplateColumns: `repeat(${
                          formData.imagePosition === "side_to_side" ? 2 : 1
                        }, 1fr)`,
                      }}
                    >
                      {formData.abstractImages.map(
                        (abstractImage: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <img
                              src={abstractImage.preview}
                              alt={`Figure ${index + 1}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "256px",
                                objectFit: "contain",
                              }}
                            />
                            {abstractImage.title && (
                              <div
                                style={{
                                  marginTop: "12pt",
                                  textAlign: "center",
                                  color: "#6B7280",
                                  fontSize: "11pt",
                                  fontStyle: "normal",
                                  fontWeight: 400,
                                }}
                              >
                                {abstractImage.title}
                              </div>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </section>
                )}

              {/* References */}
              {formData.references && formData.references.length > 0 && (
                <section style={{ marginTop: "12pt" }}>
                  <div style={{ fontFamily: "Times New Roman" }}>
                    <span style={{ fontSize: "11pt", fontWeight: 700 }}>
                      References:
                    </span>
                    <ol style={{ marginTop: "6pt" }}>
                      {formData.references.map(
                        (reference: string, index: number) => (
                          <li
                            key={index}
                            style={{
                              fontSize: "11pt",
                              marginBottom: "4pt",
                              textAlign: "justify",
                            }}
                          >
                            {reference}
                          </li>
                        ),
                      )}
                    </ol>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-2 text-blue-900 bg-blue-100 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Abstract Content
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-6 py-2 text-white bg-blue-900 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-w-[180px]"
        >
          <div className="flex items-center justify-center space-x-2 w-full">
            {isSubmitting ? (
              <span className="py-0.5">
                <ArrowPathIcon className="h-4 w-4 animate-spin text-white" />
              </span>
            ) : (
              <span className="text-sm font-normal leading-5 text-white">
                Submit
              </span>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

export default Step4;
